import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { RouteComponentProps } from "../../models/RouteComponent";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { getUsage } from "../../service/billingService";
import { dropdownViewGet } from "../../service/api-service";
import {USAGE_TYPE, formatLocalDateAsUtc} from "./common";
import {InputText} from "primereact/inputtext";

interface Client {
  id: string;
  name: string;
}

interface UsageData {
  date: string;
  client_name: string;
  usage_type: number;
  cost_client: string;
  cost_system: string;
}

const systemClientName: string = 'SYSTEM';

export default function BillingUsageTable(props: RouteComponentProps) {
  // State for filters
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return [firstDay, lastDay];
  });
  const [groupByDate, setGroupByDate] = useState<string>('month');
  const [groupByDimension, setGroupByDimension] = useState<string | null>('client');
  const [clientId, setClientId] = useState<string>('');
  const [usageType, setUsageType] = useState<number | null>(null);
  const [clients, setClients] = useState<Client[]>([]);

  // State for data
  const [data, setData] = useState<UsageData[]>([]);
  const [totalClient, setTotalClient] = useState<number>(0);
  const [totalSystem, setTotalSystem] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const dateOptions = [
    { label: 'Month', value: 'month' },
    { label: 'Day', value: 'day' },
  ];

  const dimensionOptions = [
    { label: 'Client', value: 'client' },
    { label: 'Usage Type', value: 'usageType' }
  ];

  const generateFileName = () => {
    const now = new Date();
    const parts = [now.toISOString()];
    parts.push(`by-${groupByDate}`);
    if (clientId) {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        parts.push(`client-${client.name}`);
      }
    }
    if (usageType !== null) {
      const type = USAGE_TYPE.find(t => t.value === usageType);
      if (type) {
        parts.push(`type-${type.label}`);
      }
    }
    if (groupByDimension) {
      parts.push(`group-${groupByDimension}`);
    }
    parts.push(`from-${formatLocalDateAsUtc(dateRange[0], false)}`);
    parts.push(`to-${formatLocalDateAsUtc(dateRange[1], true)}`);

    return `usage-report-${parts.join('--').replace(/[:.]/g, '-')}.csv`;
  };

  useEffect(() => {
    dropdownViewGet("client").then((data: Client[]) => {
      const allClients = [{id: 'null', name: systemClientName}, ...data];
      setClients(allClients);
    }).catch((error: Error) => {
      console.error("Error fetching clients:", error);
      setClients([]);
    });
  }, []);

  const handleDownloadCsv = () => {
    if (data.length === 0) return;

    // Determine visible columns and their headers
    const headers = ['Date'];
    if (data.some(row => row.client_name)) headers.push('Client Name');
    if (data.some(row => row.usage_type)) headers.push('Usage Type');
    headers.push('Client Cost', 'System Cost');

    const csvRows = [headers];

    data.forEach(row => {
      const rowData = [row.date];
      if (headers.includes('Client Name')) rowData.push(row.client_name || '');
      if (headers.includes('Usage Type')) {
        const usageTypeLabel = USAGE_TYPE.find(opt => opt.value === row.usage_type)?.label || row.usage_type.toString();
        rowData.push(usageTypeLabel);
      }
      rowData.push(parseFloat(row.cost_client).toFixed(2));
      rowData.push(parseFloat(row.cost_system).toFixed(2));
      csvRows.push(rowData);
    });

    // Convert to CSV string
    const csvContent = csvRows.map(row => row.join(',')).join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = generateFileName();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const fetchData = async () => {
    const [dateFrom, dateTo] = dateRange;
    if(!dateFrom || !dateTo)
      return;

    setLoading(true);
    try {
      const response = await getUsage({
        dateFrom: formatLocalDateAsUtc(dateFrom, false),
        dateTo: formatLocalDateAsUtc(dateTo, true),
        groupByDate,
        groupByDimension: groupByDimension || undefined,
        clientId: clientId || undefined,
        usageType: usageType?.toString()
      });


      let clientTotal = 0;
      let systemTotal = 0;
      const data = [];
      for (const row of response.content.rows) {
        clientTotal += parseFloat(row.cost_client);
        systemTotal += parseFloat(row.cost_system);

        if ('client_id' in row && row.client_id === null) {
          row.client_name = systemClientName;
        }

        data.push(row);
      }
      setData(data);
      setTotalClient(clientTotal);
      setTotalSystem(systemTotal);
    } catch (error) {
      console.error('Error fetching usage data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, groupByDate, groupByDimension, clientId, usageType]);

  const renderHeader = () => {
    return (
      <div style={{display: 'flex', justifyContent: 'end'}}>
        <Button type="button" icon="pi pi-download" label="Download CSV"
                className="p-button-outlined" size="small"
                style={{ fontSize: 'small', marginRight: 10, color: "#6030ff" }}
                onClick={handleDownloadCsv} />
      </div>
    )
  }

  const footerStyle = {
    backgroundColor: '#f8f9fa',
    fontWeight: 'bold',
    color: '#495057'
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: '20px', padding: '20px' }}>
      {/* Data Table Column */}
      <div>
        <DataTable
          value={data}
          loading={loading}
          className="p-datatable-sm"
          header={renderHeader()}
        >
          <Column
            field="date"
            header="Date"
            sortable
            footer={<div style={footerStyle}>Totals</div>}
          />
          {data.some(row => row.client_name) && (
            <Column
              field="client_name"
              header="Client Name"
              sortable
              footer={<div style={footerStyle}></div>}
            />
          )}
          {data.some(row => row.usage_type) && (
            <Column
              field="usage_type"
              header="Usage Type"
              sortable
              body={(rowData) => {
                const option = USAGE_TYPE.find(opt => opt.value === rowData.usage_type);
                return option ? option.label : rowData.usage_type;
              }}
              footer={<div style={footerStyle}></div>}
            />
          )}
          <Column
            field="cost_system"
            header="System Cost"
            sortable
            body={(rowData) => `R${parseFloat(rowData.cost_system).toFixed(2)}`}
            footer={<div style={footerStyle}>R{totalSystem.toFixed(2)}</div>}
          />
          <Column
            field="cost_client"
            header="Client Cost"
            sortable
            body={(rowData) => rowData.client_name === "SYSTEM" ? '' : `R${parseFloat(rowData.cost_client).toFixed(2)}`}
            footer={<div style={footerStyle}>R{totalClient.toFixed(2)}</div>}
          />
        </DataTable>
      </div>

      {/* Filters Column */}
      <div style={{
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '15px'
      }}>
        <h3>Filters</h3>

        <div className="field">
          <label>Date Range (UTC)</label>
          <Calendar
            value={dateRange}
            onChange={(e) => setDateRange(e.value as any)}
            selectionMode="range"
            showIcon
            className="w-full"
          />
        </div>

        <div className="field">
          <label>Group by Date</label>
          <Dropdown
            value={groupByDate}
            options={dateOptions}
            onChange={(e) => setGroupByDate(e.value)}
            className="w-full"
          />
        </div>

        <div className="field">
          <label>Group by Dimension</label>
          <Dropdown
            value={groupByDimension}
            options={dimensionOptions}
            onChange={(e) => setGroupByDimension(e.value)}
            className="w-full"
            placeholder="Group by specific dimension"
            showClear
          />
        </div>

        <div className="field">
          <label>Client</label>
          <Dropdown
            value={clientId}
            options={clients}
            onChange={(e) => setClientId(e.value)}
            optionLabel="name"
            optionValue="id"
            className="w-full"
            placeholder="Filter on a client"
            showClear
          />
        </div>

        <div className="field">
          <label>Usage Type</label>
          <Dropdown
            value={usageType}
            options={USAGE_TYPE}
            onChange={(e) => setUsageType(e.value)}
            className="w-full"
            placeholder="Filter on usage type"
            showClear
          />
        </div>
      </div>
    </div>
  );
}
